import React, { useState, useEffect } from "react";
import cn from "classnames";
import { formatDistance, formatDistanceToNow } from "date-fns";
import { Link } from "react-router-dom";

import { Button } from "../../common/button";
import { Preloader } from "../../common/preloader";
import { ReactComponent as SaveIcon } from "../../../media/icons/heart.svg";
import { ReactComponent as PhoneIcon } from "../../../media/icons/phone.svg";
import { ReactComponent as LocationIcon } from "../../../media/icons/linear-location.svg";
import { ReactComponent as ShareIcon } from "../../../media/icons/share.svg";
import defaultImage from "../../../media/images/defaultImage.png";
import styles from "./profileListing.module.css";

const ProfileListing = ({ isExpand, list, addToFavorite, removeFavorite }) => {
  return (
    <div className={cn(styles.flex, isExpand ? styles.flexExpand : "")}>
      {list.map((item, index) => {
        return (
          <Profile
            isExpand={isExpand}
            item={item}
            addToFavorite={addToFavorite}
            removeFavorite={removeFavorite}
            /* eslint-disable-next-line react/no-array-index-key */
            key={`${item.title}-${item.added}-${index}`}
          />
        );
      })}
    </div>
  );
};

const Profile = ({ item, isExpand, removeFavorite, addToFavorite }) => {
  const [loading, setLoading] = useState(false);
  const [isFavorite, setIsFavorite] = useState(item.bookmarked === "true");

  const toggleFavorite = async listingId => {
    if (loading) return;

    setLoading(true);
    try {
      if (isFavorite) {
        const res = await removeFavorite(listingId);
        if (res) {
          setIsFavorite(false);
        }
      } else {
        const res = await addToFavorite(listingId);
        if (res) {
          setIsFavorite(true);
        }
      }
    } catch (error) {
      console.error("Favorite toggle error:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (item.bookmarked === "true") {
      setIsFavorite(true);
    }
  }, [item.bookmarked]);

  const { licensed_duration: licensedDuration } = item;
  const licensedSinceText = licensedDuration
    ? `${
        licensedDuration.through
          ? formatDistance(new Date(licensedDuration.since), new Date(licensedDuration.through))
          : formatDistanceToNow(new Date(licensedDuration.since))
      } Licensed`
    : null;

  return (
    <div className={cn(styles.tile, isExpand ? styles.tileExpand : "")} key={item.title}>
      <div className={styles.favorites}>
        <Button
          className={cn(styles.favoritesButton, isFavorite ? styles.favoritesButtonActive : "")}
          onClick={() => toggleFavorite(item.listing_id)}
          disabled={loading}
        >
          {loading ? <Preloader className={styles.spinner} /> : <SaveIcon />}
        </Button>
      </div>

      <div className={styles.share}>
        <Button className={styles.shareButton}>
          <ShareIcon />
        </Button>
      </div>

      <div className={styles.heading}>
        <Link to={`/public-profile/${item.listing_id}`} className={styles.tileImage}>
          <img src={item.cover_image_url || defaultImage} alt="Profile banner" />
        </Link>
        <div className={styles.headingContent}>
          <div className={styles.headingFlex}>
            <Link to={`/public-profile/${item.listing_id}`} className={styles.tileTitle}>
              {item.attorney}
            </Link>
            {item.is_premium && <span className={cn(styles.sponsor, styles.sponsorTablet)}>Sponsor</span>}
          </div>
          {item.is_premium && <span className={cn(styles.sponsor, styles.sponsorMobile)}>Sponsor</span>}

          {item.lawfirms.length ? (
            <div className={cn(styles.locationContent, styles.locationContentMobile)}>
              <LocationIcon />
              {item.lawfirms.map(locationItem => (
                <p className={styles.location} key={`mobile-${locationItem.city}-${locationItem.state}-${locationItem.prkey}`}>{`${
                  locationItem.city
                }${locationItem.state ? `, ${locationItem.state}` : ""}`}</p>
              ))}
            </div>
          ) : null}

          <div className={styles.tileInfoTablet}>
            {item.lawfirms.length ? (
              <div className={styles.locationContent}>
                <LocationIcon />
                {item.lawfirms.map(locationItem => (
                  <p className={styles.location} key={`tablet-${locationItem.city}-${locationItem.state}-${locationItem.prkey}`}>{`${
                    locationItem.city
                  }${locationItem.state ? `, ${locationItem.state}` : ""}`}</p>
                ))}
              </div>
            ) : null}

            {item.practices.length ? (
              <div className={styles.practicesContent}>
                {item.practices.slice(0, 3).map((practiceItem, index) => (
                  <p className={styles.practices} key={practiceItem.practice_title}>
                    {`${index !== 0 ? ", " : ""}${practiceItem.practice_title}`}
                  </p>
                ))}
                {item.practices.length > 3 ? <p className={styles.practices}>... </p> : null}
              </div>
            ) : null}

            <div className={styles.tileFlex}>
              {licensedSinceText && <p className={styles.experience}>{licensedSinceText}</p>}
              {item.phone ? (
                <a href={`tel:${item.phone}`} className={styles.phone}>
                  <PhoneIcon /> {item.phone}
                </a>
              ) : null}
            </div>
          </div>
        </div>
      </div>

      <div className={styles.tileInfoMobile}>
        {item.practices.length ? (
          <div className={styles.practicesContent}>
            {item.practices.slice(0, 3).map((practiceItem, index) => (
              <p className={styles.practices} key={practiceItem.practice_title}>
                {`${index !== 0 ? ", " : ""}${practiceItem.practice_title}`}
              </p>
            ))}
            {item.practices.length > 3 ? <p className={styles.practices}>... </p> : null}
          </div>
        ) : null}
        <div className={styles.tileFlex}>
          {licensedSinceText && <p className={styles.experience}>{licensedSinceText}</p>}
          {item.phone ? (
            <a href={`tel:${item.phone}`} className={styles.phone}>
              <PhoneIcon /> {item.phone}
            </a>
          ) : null}
        </div>
      </div>

      {item.phone ? (
        <p className={styles.tileDescription}>
          <a href={`tel:${item.phone}`} className={styles.mobilePhone}>
            <PhoneIcon /> {item.phone}
          </a>
        </p>
      ) : null}
    </div>
  );
};

export default ProfileListing;
